import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import dayjs from 'dayjs';
import Logo from '../../../assets/images/Logo.svg';
import { ServiceType } from '../../../happypaws-shared/src/Services/services';
import { useSetAvailability } from '../../../hooks/sitter/useSetAvailability';
import { useSetPricing } from '../../../hooks/sitter/useSetPricing';
import { useUpdateSitterProfile } from '../../../hooks/sitter/useUpdateSitterProfile';
import { geocodeAddress } from '../../../utils/geocode';
import StepFour, { Step4State } from './components/StepFour';
import StepOne from './components/StepOne';
import StepTree, { Step3State } from './components/StepTree';
import StepTwo, { Step2State } from './components/StepTwo';

const BoardingSitters: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { mutate: updateSitterProfile, isPending: isUpdating } = useUpdateSitterProfile();
  const { mutate: setAvailability, isPending: isSettingAvailability } = useSetAvailability();
  const { mutate: setPricing, isPending: isSettingPricing } = useSetPricing();

  const isPending = isUpdating || isSettingAvailability || isSettingPricing;

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [step2, setStep2] = useState<Step2State>({
    profilePhoto: null,
    username: user ? `${user.firstName.toLowerCase()}${user.lastName.toLowerCase()}` : '',
    bio: '',
    education: '',
    experience: null,
    galleryImages: [null, null, null, null],
    verification: '',
    city: '',
    state: '',
    zipCode: '',
  });

  const [step3, setStep3] = useState<Step3State>({
    serviceTypes: [],
    petPreferences: [],
  });

  const [step4, setStep4] = useState<Step4State>({
    availabilityDates: {},
    pricingItems: [{ service: ServiceType.PET_SITTING, priceMin: 10, priceMax: 50 }],
    currentMonth: dayjs(),
  });

  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});

  const clearError = (key: string) => {
    setStepErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 2) {
      if (!step2.username.trim()) errors.username = 'Username is required';
      if (!step2.experience) errors.experience = 'Experience is required';
    }

    if (step === 3) {
      if (step3.serviceTypes.length === 0)
        errors.serviceTypes = 'Please select at least one service';
      if (step3.petPreferences.length === 0)
        errors.petPreferences = 'Please select at least one animal preference';
    }

    if (step === 4) {
      const hasComplete = step4.pricingItems.some((item) => item.service && item.priceMax > 0);
      if (!hasComplete) errors.pricingItems = 'Please fill in at least one pricing item';
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    let coords: { lat: number; lng: number } | null = null;

    if (step2.city && step2.state) {
      coords = await geocodeAddress(step2.city, step2.state, step2.zipCode);
    }

    const pricingPayload = step4.pricingItems
      .filter((item) => item.service && item.priceMax > 0)
      .map((item) => ({ service: item.service, priceMin: item.priceMin, priceMax: item.priceMax }));

    const availabilityPayload = Object.entries(step4.availabilityDates).map(
      ([date, isAvailable]) => ({ date, isAvailable })
    );

    updateSitterProfile(
      {
        username: step2.username,
        bio: step2.bio,
        education: step2.education,
        experience: step2.experience,
        verification: step2.verification,
        serviceTypes: step3.serviceTypes,
        petPreferences: step3.petPreferences,
        galleryImages: [],
        profilePhotoUrl: null,
        ...(coords && { lat: coords.lat, lng: coords.lng }),
      },
      {
        onSuccess: () => {
          if (availabilityPayload.length > 0) {
            setAvailability(
              { dates: availabilityPayload },
              {
                onSuccess: () => {
                  if (pricingPayload.length > 0) {
                    setPricing(
                      { items: pricingPayload },
                      {
                        onSuccess: () => navigate('/sitter-dashboard'),
                        onError: (error) => console.error('Failed to set pricing:', error),
                      }
                    );
                  } else {
                    navigate('/sitter-dashboard');
                  }
                },
                onError: (error) => console.error('Failed to set availability:', error),
              }
            );
          } else {
            if (pricingPayload.length > 0) {
              setPricing(
                { items: pricingPayload },
                {
                  onSuccess: () => navigate('/sitter-dashboard'),
                  onError: (error) => console.error('Failed to set pricing:', error),
                }
              );
            } else {
              navigate('/sitter-dashboard');
            }
          }
        },
        onError: (error) => console.error('Failed to update sitter profile:', error),
      }
    );
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderProgressIndicator = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 6,
        gap: 2,
      }}
    >
      {Array.from({ length: totalSteps }).map((_, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              backgroundColor:
                index + 1 === currentStep
                  ? '#178B8E'
                  : index + 1 < currentStep
                    ? '#50d9d7'
                    : '#B8D8D8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '1.2rem',
              transition: 'all 0.3s ease',
            }}
          >
            {index + 1}
          </Box>
          {index < totalSteps - 1 && (
            <Box
              sx={{
                width: 120,
                height: 4,
                backgroundColor: index + 1 < currentStep ? '#50d9d7' : '#B8D8D8',
                transition: 'all 0.3s ease',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );

  const renderStep1 = () => (
    <>
      <StepOne
        // @ts-ignore
        currentStep={currentStep}
        registrationData={user}
        handleNext={handleNext}
      />
    </>
  );

  const renderStep2 = () => (
    <>
      <StepTwo
        currentStep={currentStep}
        handleNext={handleNext}
        handleBack={handleBack}
        step2={step2}
        setStep2={setStep2}
        errors={stepErrors}
        clearError={clearError}
      />
    </>
  );

  const renderStep3 = () => (
    <>
      <StepTree
        currentStep={currentStep}
        handleNext={handleNext}
        handleBack={handleBack}
        step3={step3}
        setStep3={setStep3}
        errors={stepErrors}
        clearError={clearError}
      />
    </>
  );

  const renderStep4 = () => (
    <>
      <StepFour
        currentStep={currentStep}
        step4={step4}
        setStep4={setStep4}
        serviceTypes={step3.serviceTypes}
        handleNext={handleNext}
        handleBack={handleBack}
        isPending={isPending}
        errors={stepErrors}
        clearError={clearError}
      />
    </>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#FAF8F5',
        py: 2,
      }}
    >
      <Container maxWidth="lg">
        {/* Header with Skip */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <img src={Logo} alt="HappyPaws Logo" style={{ width: '205px', height: '45px' }} />
        </Box>

        {/* Progress Indicator */}
        {renderProgressIndicator()}

        {/* Step Content */}
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            p: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </Box>
      </Container>
    </Box>
  );
};

export default BoardingSitters;
